import { Select } from "antd";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserReportData, UserReportRoleDict, UserReportType } from "..";
import { selectGroups } from "../../../../features/groups/groupSlice";
import {
  CHART_COLORS,
  IReportDataset,
} from "../../../../features/reports/common";
import { GenderDict, GenderEnum } from "../../../../features/users/usersSlice";
import LoadingOverlay from "../../../common/LoadingOverlay";
import { BarChartGrouped } from "../../common/BarChartGrouped";
import styles from "./index.module.scss";
import { getTranslations } from "../../../../features/translations/translationsUtils";
import { AgeGroupsEnum, getSelectorsAndSetters } from "../../common/interfaces";
import { ReportTypes } from "../../MenuAndRoutes";

const { Option } = Select;

interface AgeInterval {
  min: number;
  max: number;
}

const AgeGroupDict: () => Record<AgeGroupsEnum, string> = () => ({
  young: getTranslations('REPORT_YOUNG_AGES'),
  youngAdult: getTranslations('REPORT_YOUNG_ADULTS_AGES'),
  adult: getTranslations('REPORT_ADULTS_AGES'),
  mature: getTranslations('REPORT_MATURE_AGES'),
});

const AgeGroups: Record<AgeGroupsEnum, AgeInterval> = {
  young: { min: 18, max: 30 },
  youngAdult: { min: 31, max: 50 },
  adult: { min: 51, max: 70 },
  mature: { min: 71, max: 120 },
};

const getData = (list: UserReportData[], type: UserReportType) => {
  const userList = list.filter((user) => user.userType === type || (!user.userType && type === UserReportType.Student));
  const activeUsers = userList.filter((user) => user.active);
  return [activeUsers.length, userList.length - activeUsers.length];
};

const checkAgeRange = (age: number, ageGroup?: AgeGroupsEnum) => {
  if (!ageGroup) {
    return false;
  }
  return age >= AgeGroups[ageGroup].min && age <= AgeGroups[ageGroup].max;
};

const filterCondition = (
  user: UserReportData,
  group?: string,
  gender?: GenderEnum,
  ageGroup?: AgeGroupsEnum
) => {
  return (
    (!group || user.groupId === group) &&
    (!gender || user.gender === gender) &&
    (!ageGroup || checkAgeRange(user.age, ageGroup))
  );
};

interface Props {
  enabledUsers: UserReportData[];
  isLoading: boolean;
  companyId?: string;
}

export const UsersStateChart: React.FunctionComponent<Props> = ({
  enabledUsers,
  isLoading,
  companyId,
}) => {
  const selectorAndFilter = getSelectorsAndSetters(ReportTypes.Users);

  const dispatch = useDispatch();
  const groupsList = useSelector(selectGroups);

  const onGroupChange = (value: string) => dispatch(selectorAndFilter?.setGroupId(value));
  const onGenderChange = (value: GenderEnum) => dispatch(selectorAndFilter?.setUsersGender(value));
  const onAgeGroupChange = (value: AgeGroupsEnum) => dispatch(selectorAndFilter?.setUsersAge(value));

  const filter = useSelector(selectorAndFilter?.selectFilter ?? (() => null));
  useEffect(() => {
    selectorAndFilter?.setGroupId("");
    selectorAndFilter?.setUsersGender(undefined);
    selectorAndFilter?.setUsersAge(undefined);
  }, [selectorAndFilter, filter?.companyId]);

  const filteredEnabledUsers = enabledUsers.filter((user) =>
    filterCondition(user, filter?.groupId, filter?.usersGender, filter?.usersAge)
  );

  const filteredGroupList = companyId
    ? groupsList.filter((group) => group.company === companyId)
    : groupsList;

  const userTypeData: IReportDataset[] = [
    {
      label: getTranslations(UserReportRoleDict.Supervisor),
      data: getData(filteredEnabledUsers, UserReportType.Supervisor),
      color: CHART_COLORS[1],
    },
    {
      label: getTranslations(UserReportRoleDict.CompanyAdmin),
      data: getData(filteredEnabledUsers, UserReportType.CompanyAdmin),
      color: CHART_COLORS[0],
    },
    {
      label: getTranslations(UserReportRoleDict.Student),
      data: getData(filteredEnabledUsers, UserReportType.Student),
      color: CHART_COLORS[2],
    },
  ];

  return (
    <>
      <div className={styles.title}>
        {getTranslations('REPORT_ACTIVE_IDLE_USER_COUNT')}
      </div>
      {isLoading ? (
        <LoadingOverlay inline height={420} spinning />
      ) : (
        <>
          <Select
            allowClear
            value={filter?.groupId}
            onChange={onGroupChange}
            className={filter?.groupId ? styles.activeSelector : styles.selector}
            style={{ width: 100, margin: "7px 8px" }}
            placeholder={getTranslations('REPORT_GROUP')}
            dropdownMatchSelectWidth={200}
          >
            {filteredGroupList.map((group) => (
              <Option value={group._id} key={group._id}>
                {group.name}
              </Option>
            ))}
          </Select>

          <Select
            allowClear
            value={filter?.usersGender}
            onChange={onGenderChange}
            className={filter?.usersGender ? styles.activeSelector : styles.selector}
            style={{ width: 100, margin: "7px 8px" }}
            placeholder={getTranslations('REPORT_GENDER')}
            dropdownMatchSelectWidth={200}
          >
            {Object.values(GenderEnum).map((gender) => (
              <Option value={gender} key={gender}>
                {GenderDict()[gender]}
              </Option>
            ))}
          </Select>

          <Select
            allowClear
            value={filter?.usersAge}
            onChange={onAgeGroupChange}
            className={
              filter?.usersAge ? styles.activeSelector : styles.selector
            }
            style={{ width: 100, margin: "7px 8px" }}
            placeholder={getTranslations('REPORT_AGE')}
            dropdownMatchSelectWidth={200}
          >
            {Object.values(AgeGroupsEnum).map((ageGroup) => (
              <Option value={ageGroup} key={ageGroup}>
                {AgeGroupDict()[ageGroup]}
              </Option>
            ))}
          </Select>

          <BarChartGrouped
            datasets={userTypeData}
            labels={[getTranslations('REPORT_ACTIVE'), getTranslations('REPORT_IDLE')]}
            labelColumnTitle="my label"
            fetching={isLoading}
            axisMaxValue={
              enabledUsers.length > 0 ? enabledUsers.length : undefined
            }
          />
        </>
      )}
    </>
  );
};
